// src/app/api/progressReportApi.ts
//
// RTK Query slice for:
//   - Parent reading published progress reports (/api/parent/progress/*)
//   - Admin managing reports, attendance, schedules (/api/admin/progress/*)

import { baseApi } from "./baseApi";
import type { StudentTrack } from "./authApi";

/* ─────────────────────────────────────────────────────────────────
   SHARED TYPES
───────────────────────────────────────────────────────────────── */

export type TechnicalRating =
  | "emerging"
  | "developing"
  | "strong"
  | "excellent";
export type CommunicationRating =
  | "emerging"
  | "developing"
  | "strong"
  | "excellent";
export type EffortRating = "low" | "moderate" | "high";
export type AttendanceStatus = "present" | "absent" | "excused" | "late";
export type Quarter = "Q1" | "Q2" | "Q3";

/* ─────────────────────────────────────────────────────────────────
   PROGRESS REPORT
───────────────────────────────────────────────────────────────── */

export interface ProgressReport {
  id: string;
  enrolment: string;
  child: string | { id: string; firstName: string; lastName: string } | null;
  childName: string | null;
  parent: string;
  authorName: string;
  weekNumber: number;
  reportDate: string;
  reportDateLabel: string | null;
  academicYear: string;
  quarter: Quarter;
  track: StudentTrack;
  sessionTopic: string | null;
  whatWasBuilt: string | null;
  currentProject: string | null;
  skillsDemonstrated: string[];
  areasToStrengthen: string[];
  technicalRating: TechnicalRating;
  communicationRating: CommunicationRating;
  effortRating: EffortRating;
  progressPercent: number;
  tutorNote: string | null;
  parentAction: string | null;
  requiresParentAttention: boolean;
  attended: boolean;
  isPublished: boolean;
  readByParent: boolean;
  readAt: string | null;
  createdAt: string;
  updatedAt: string;
  // Only on admin detail GET
  attendance?: AttendanceRecord | null;
}

/* ─────────────────────────────────────────────────────────────────
   ATTENDANCE
───────────────────────────────────────────────────────────────── */

export interface AttendanceRecord {
  id: string;
  enrolment: string;
  child: string | null;
  childName: string | null;
  parent: string;
  markedBy: string | null;
  sessionDate: string;
  sessionDateLabel: string | null;
  weekNumber: number;
  academicYear: string;
  quarter: Quarter;
  status: AttendanceStatus;
  note: string | null;
  parentNotified: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface MarkAttendanceBody {
  enrolmentId: string;
  weekNumber: number;
  sessionDate: string;
  status: AttendanceStatus;
  note?: string;
}

export interface BatchAttendanceBody {
  academicYear: string;
  quarter: Quarter;
  weekNumber: number;
  sessionDate: string;
  records: Array<{
    enrolmentId: string;
    status: AttendanceStatus;
    note?: string;
  }>;
}

/* ─────────────────────────────────────────────────────────────────
   QUARTER SCHEDULE
───────────────────────────────────────────────────────────────── */

export interface WeekSession {
  weekNumber: number;
  sessionDate: string;
  sessionLabel: string | null;
  topic: string | null;
  isActive: boolean;
}

export interface QuarterSchedule {
  _id: string;
  academicYear: string;
  quarter: Quarter;
  track: StudentTrack;
  startDate: string;
  endDate: string;
  showcaseDate: string | null;
  showcaseLabel: string | null;
  sessions: WeekSession[];
  createdBy: string | null;
  isActive: boolean;
  notes: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface CreateScheduleBody {
  academicYear: string;
  quarter: Quarter;
  track: StudentTrack;
  startDate: string;
  endDate: string;
  showcaseDate?: string;
  showcaseLabel?: string;
  sessions: Array<{
    weekNumber: number;
    sessionDate: string;
    sessionLabel?: string;
    topic?: string;
  }>;
  notes?: string;
}

/* ─────────────────────────────────────────────────────────────────
   REPORT REQUEST / FILTER TYPES
───────────────────────────────────────────────────────────────── */

export interface CreateReportBody {
  enrolmentId: string;
  weekNumber: number;
  reportDate: string;
  sessionTopic?: string;
  whatWasBuilt?: string;
  currentProject?: string;
  skillsDemonstrated?: string[];
  areasToStrengthen?: string[];
  technicalRating?: TechnicalRating;
  communicationRating?: CommunicationRating;
  effortRating?: EffortRating;
  progressPercent?: number;
  tutorNote?: string;
  parentAction?: string;
  requiresParentAttention?: boolean;
  attended?: boolean;
  isPublished?: boolean;
}

export type UpdateReportBody = Partial<
  Omit<CreateReportBody, "enrolmentId" | "weekNumber">
>;

export interface GetReportsParams {
  enrolmentId?: string;
  childId?: string;
  quarter?: Quarter;
  academicYear?: string;
  weekNumber?: number;
  track?: StudentTrack;
  isPublished?: boolean;
  page?: number;
  limit?: number;
}

export interface PaymentDuePreviewItem {
  enrolmentId: string;
  childFirstName: string;
  childLastName: string;
  parentEmail: string;
  parentFirstName: string;
  billingPeriod: string;
  feeAmount: number;
  nextDueDate: string;
  nextDueLabel: string;
  quarter: Quarter;
  academicYear: string;
  track: StudentTrack;
}

/* ─────────────────────────────────────────────────────────────────
   RESPONSE WRAPPERS
───────────────────────────────────────────────────────────────── */

interface SingleResponse<T> {
  success: boolean;
  message: string;
  data: T;
}
interface ListResponse<T> {
  success: boolean;
  count: number;
  data: T[];
}
interface PagedListResponse<T> extends ListResponse<T> {
  total: number;
  page: number;
  pages: number;
}

/* ─────────────────────────────────────────────────────────────────
   API SLICE
───────────────────────────────────────────────────────────────── */

export const progressReportApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    /* ── PARENT — read published reports ─────────────────────────── */

    /** All published reports for all of this parent's children */
    getProgressReports: builder.query<
      ListResponse<ProgressReport> & { unreadCount: number },
      {
        childId?: string;
        quarter?: Quarter;
        academicYear?: string;
        unreadOnly?: boolean;
      }
    >({
      query: (params = {}) => ({ url: "/parent/progress", params }),
      providesTags: (result) =>
        result
          ? [
              ...result.data.map(({ id }) => ({
                type: "ProgressReports" as const,
                id,
              })),
              { type: "ProgressReports" as const, id: "LIST" },
            ]
          : [{ type: "ProgressReports" as const, id: "LIST" }],
    }),

    /** Published reports for a single child */
    getChildProgressReports: builder.query<
      ListResponse<ProgressReport> & { childName?: string },
      string
    >({
      query: (childId) => `/parent/progress/${childId}`,
      providesTags: (_r, _e, childId) => [
        { type: "ProgressReports" as const, id: `child-${childId}` },
      ],
    }),

    /** Mark a report as read by the parent */
    markReportRead: builder.mutation<SingleResponse<null>, string>({
      query: (reportId) => ({
        url: `/parent/progress/${reportId}/read`,
        method: "PATCH",
      }),
      invalidatesTags: (_r, _e, reportId) => [
        { type: "ProgressReports" as const, id: reportId },
        { type: "ProgressReports" as const, id: "LIST" },
      ],
    }),

    /* ── ADMIN — quarter schedules ───────────────────────────────── */

    adminGetSchedules: builder.query<
      ListResponse<QuarterSchedule>,
      {
        academicYear?: string;
        quarter?: Quarter;
        track?: StudentTrack;
      }
    >({
      query: (params = {}) => ({ url: "/admin/progress/schedule", params }),
      providesTags: [{ type: "QuarterSchedules" as const, id: "LIST" }],
    }),

    adminGetSchedule: builder.query<SingleResponse<QuarterSchedule>, string>({
      query: (id) => `/admin/progress/schedule/${id}`,
      providesTags: (_r, _e, id) => [{ type: "QuarterSchedules" as const, id }],
    }),

    adminCreateSchedule: builder.mutation<
      SingleResponse<QuarterSchedule>,
      CreateScheduleBody
    >({
      query: (body) => ({
        url: "/admin/progress/schedule",
        method: "POST",
        body,
      }),
      invalidatesTags: [{ type: "QuarterSchedules" as const, id: "LIST" }],
    }),

    adminUpdateSchedule: builder.mutation<
      SingleResponse<QuarterSchedule>,
      { id: string } & Partial<CreateScheduleBody>
    >({
      query: ({ id, ...body }) => ({
        url: `/admin/progress/schedule/${id}`,
        method: "PATCH",
        body,
      }),
      invalidatesTags: (_r, _e, { id }) => [
        { type: "QuarterSchedules" as const, id },
        { type: "QuarterSchedules" as const, id: "LIST" },
      ],
    }),

    adminDeleteSchedule: builder.mutation<SingleResponse<null>, string>({
      query: (id) => ({
        url: `/admin/progress/schedule/${id}`,
        method: "DELETE",
      }),
      invalidatesTags: [{ type: "QuarterSchedules" as const, id: "LIST" }],
    }),

    /* ── ADMIN — attendance ──────────────────────────────────────── */

    adminGetAttendance: builder.query<
      ListResponse<AttendanceRecord>,
      {
        enrolmentId?: string;
        childId?: string;
        weekNumber?: number;
        quarter?: Quarter;
        academicYear?: string;
        status?: AttendanceStatus;
      }
    >({
      query: (params = {}) => ({ url: "/admin/progress/attendance", params }),
      providesTags: [{ type: "Attendance" as const, id: "LIST" }],
    }),

    adminMarkAttendance: builder.mutation<
      SingleResponse<AttendanceRecord>,
      MarkAttendanceBody
    >({
      query: (body) => ({
        url: "/admin/progress/attendance",
        method: "POST",
        body,
      }),
      invalidatesTags: [
        { type: "Attendance" as const, id: "LIST" },
        { type: "ProgressReports" as const, id: "LIST" },
      ],
    }),

    adminMarkAttendanceBatch: builder.mutation<
      SingleResponse<any>,
      BatchAttendanceBody
    >({
      query: (body) => ({
        url: "/admin/progress/attendance/batch",
        method: "POST",
        body,
      }),
      invalidatesTags: [
        { type: "Attendance" as const, id: "LIST" },
        { type: "ProgressReports" as const, id: "LIST" },
      ],
    }),

    adminUpdateAttendance: builder.mutation<
      SingleResponse<AttendanceRecord>,
      {
        id: string;
        status?: AttendanceStatus;
        note?: string;
        parentNotified?: boolean;
      }
    >({
      query: ({ id, ...body }) => ({
        url: `/admin/progress/attendance/${id}`,
        method: "PATCH",
        body,
      }),
      invalidatesTags: [{ type: "Attendance" as const, id: "LIST" }],
    }),

    /* ── ADMIN — progress reports ────────────────────────────────── */

    adminGetReports: builder.query<
      PagedListResponse<ProgressReport>,
      GetReportsParams
    >({
      query: (params = {}) => ({ url: "/admin/progress/reports", params }),
      providesTags: (result) =>
        result
          ? [
              ...result.data.map(({ id }) => ({
                type: "ProgressReports" as const,
                id,
              })),
              { type: "ProgressReports" as const, id: "ADMIN_LIST" },
            ]
          : [{ type: "ProgressReports" as const, id: "ADMIN_LIST" }],
    }),

    adminGetReport: builder.query<SingleResponse<ProgressReport>, string>({
      query: (id) => `/admin/progress/reports/${id}`,
      providesTags: (_r, _e, id) => [{ type: "ProgressReports" as const, id }],
    }),

    adminCreateReport: builder.mutation<
      SingleResponse<ProgressReport>,
      CreateReportBody
    >({
      query: (body) => ({
        url: "/admin/progress/reports",
        method: "POST",
        body,
      }),
      invalidatesTags: [
        { type: "ProgressReports" as const, id: "ADMIN_LIST" },
        { type: "ProgressReports" as const, id: "LIST" },
        // If the report is created with isPublished=true, Enrolment is synced immediately.
        // Invalidate all parent-facing caches so the dashboard and child cards update.
        "ParentDashboard" as const,
        { type: "ParentChildren" as const, id: "LIST" },
        { type: "ParentEnrolments" as const, id: "LIST" },
      ],
    }),

    adminUpdateReport: builder.mutation<
      SingleResponse<ProgressReport>,
      { id: string } & UpdateReportBody
    >({
      query: ({ id, ...body }) => ({
        url: `/admin/progress/reports/${id}`,
        method: "PATCH",
        body,
      }),
      invalidatesTags: (_r, _e, { id }) => [
        { type: "ProgressReports" as const, id },
        { type: "ProgressReports" as const, id: "ADMIN_LIST" },
        { type: "ProgressReports" as const, id: "LIST" },
        // Update may change progressPercent on a published report — sync parent caches
        "ParentDashboard" as const,
        { type: "ParentChildren" as const, id: "LIST" },
        { type: "ParentEnrolments" as const, id: "LIST" },
      ],
    }),

    adminPublishReport: builder.mutation<
      SingleResponse<{ id: string; isPublished: boolean }>,
      string
    >({
      query: (id) => ({
        url: `/admin/progress/reports/${id}/publish`,
        method: "POST",
      }),
      invalidatesTags: (_r, _e, id) => [
        { type: "ProgressReports" as const, id },
        { type: "ProgressReports" as const, id: "ADMIN_LIST" },
        { type: "ProgressReports" as const, id: "LIST" },
        // Publishing syncs the Enrolment — invalidate all parent-facing
        // caches that read from the Enrolment (dashboard, child cards, enrolments list)
        "ParentDashboard" as const,
        { type: "ParentChildren" as const, id: "LIST" },
        { type: "ParentEnrolments" as const, id: "LIST" },
      ],
    }),

    adminUnpublishReport: builder.mutation<
      SingleResponse<{ id: string; isPublished: boolean }>,
      string
    >({
      query: (id) => ({
        url: `/admin/progress/reports/${id}/unpublish`,
        method: "POST",
      }),
      invalidatesTags: (_r, _e, id) => [
        { type: "ProgressReports" as const, id },
        { type: "ProgressReports" as const, id: "ADMIN_LIST" },
      ],
    }),

    adminDeleteReport: builder.mutation<SingleResponse<null>, string>({
      query: (id) => ({
        url: `/admin/progress/reports/${id}`,
        method: "DELETE",
      }),
      invalidatesTags: [{ type: "ProgressReports" as const, id: "ADMIN_LIST" }],
    }),

    /* ── ADMIN — payment due notifications ───────────────────────── */

    adminPreviewPaymentDue: builder.query<
      {
        success: boolean;
        message: string;
        daysAhead: number;
        count: number;
        data: PaymentDuePreviewItem[];
      },
      { daysAhead?: number }
    >({
      query: (params = {}) => ({
        url: "/admin/progress/notify/payment-due/preview",
        params,
      }),
      providesTags: ["PaymentDuePreview" as const],
    }),

    adminSendPaymentDueReminders: builder.mutation<
      {
        success: boolean;
        message: string;
        sent: number;
        failed: number;
        data: { sent: any[]; errors: any[] };
      },
      { daysAhead?: number }
    >({
      query: (body = {}) => ({
        url: "/admin/progress/notify/payment-due",
        method: "POST",
        body,
      }),
    }),
  }),
});

export const {
  // Parent
  useGetProgressReportsQuery,
  useGetChildProgressReportsQuery,
  useMarkReportReadMutation,
  // Admin — schedules
  useAdminGetSchedulesQuery,
  useAdminGetScheduleQuery,
  useAdminCreateScheduleMutation,
  useAdminUpdateScheduleMutation,
  useAdminDeleteScheduleMutation,
  // Admin — attendance
  useAdminGetAttendanceQuery,
  useAdminMarkAttendanceMutation,
  useAdminMarkAttendanceBatchMutation,
  useAdminUpdateAttendanceMutation,
  // Admin — reports
  useAdminGetReportsQuery,
  useAdminGetReportQuery,
  useAdminCreateReportMutation,
  useAdminUpdateReportMutation,
  useAdminPublishReportMutation,
  useAdminUnpublishReportMutation,
  useAdminDeleteReportMutation,
  // Admin — notifications
  useAdminPreviewPaymentDueQuery,
  useAdminSendPaymentDueRemindersMutation,
} = progressReportApi;
